import React, { useState, useEffect } from 'react';
import usePermission from '../../hooks/usePermission';
import { PermissionGuard, CanWrite, CanEdit, CanDelete } from '../../components/PermissionGuard';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { buildImageUrl } from '../../utils/imageUrl';
import { Filter, AlertTriangle, CheckCircle, Clock, ExternalLink, Camera, X } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

const safeDate = (d) => { try { return d && isValid(parseISO(d)) ? format(parseISO(d), 'dd MMM') : '-'; } catch { return '-'; } };
const today = () => new Date().toISOString().split('T')[0];

export default function TasksPage() {
  const { canRead, canWrite, canEdit, canDelete, hasAccess, isAdmin } = usePermission('tasks');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({ status: '', worker_id: '', department_id: '' });
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const [taskPhotos, setTaskPhotos] = useState({});
  const [showPhotosFor, setShowPhotosFor] = useState(null);

  const togglePhotos = async (taskId) => {
    if (showPhotosFor === taskId) { setShowPhotosFor(null); return; }
    if (!taskPhotos[taskId]) {
      try {
        const r = await api.get(`/tasks/${taskId}/images`);
        setTaskPhotos(p => ({ ...p, [taskId]: r.data }));
      } catch { return; }
    }
    setShowPhotosFor(taskId);
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.worker_id) params.worker_id = filters.worker_id;
    if (filters.department_id) params.department_id = filters.department_id;
    const [tRes, wRes, dRes] = await Promise.all([api.get('/tasks/all', { params }), api.get('/workers'), api.get('/departments', { params: { active_only: '1' } })]);
    setTasks(tRes.data);
    setWorkers(wRes.data.filter(w => w.is_active));
    setDepartments(dRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filters]);

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_progress').length,
    pending: tasks.filter(t => t.status === 'pending').length,
    waiting: tasks.filter(t => t.status === 'waiting').length,
    delayed: tasks.filter(t => t.status !== 'completed' && t.due_date && t.due_date < today()).length
  };

  return (
    <PermissionGuard hasAccess={hasAccess} canWrite={canWrite}>
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">All Tasks</h1>
        <p className="text-gray-500 text-sm">{tasks.length} tasks found</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'bg-gray-100 text-gray-700' },
          { label: 'Completed', value: stats.completed, color: 'bg-green-100 text-green-700' },
          { label: 'In Progress', value: stats.inProgress, color: 'bg-blue-100 text-blue-700' },
          { label: 'Pending', value: stats.pending, color: 'bg-yellow-100 text-yellow-700' },
          { label: 'Waiting', value: stats.waiting, color: 'bg-purple-100 text-purple-700' },
          { label: 'Delayed', value: stats.delayed, color: 'bg-red-100 text-red-700' },
        ].map(s => (
          <div key={s.label} className={`${s.color} rounded-xl sm:rounded-2xl p-2 sm:p-3 text-center`}>
            <p className="text-xl sm:text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
          <Filter className="w-4 h-4" /> Filters:
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by task name, project, worker..."
          className="flex-1 border border-gray-200 rounded-xl px-3 py-2 sm:py-1.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
        <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})} className="flex-1 sm:flex-initial border border-gray-200 rounded-xl px-3 py-2 sm:py-1.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
          <option value="">All Status</option>
          {['pending','in_progress','completed','on_hold','waiting'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.worker_id} onChange={e => setFilters({...filters, worker_id: e.target.value, department_id: ''})} className="flex-1 sm:flex-initial border border-gray-200 rounded-xl px-3 py-2 sm:py-1.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
          <option value="">All Workers</option>
          {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select value={filters.department_id} onChange={e => setFilters({...filters, department_id: e.target.value, worker_id: ''})} className="flex-1 sm:flex-initial border border-gray-200 rounded-xl px-3 py-2 sm:py-1.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button onClick={() => setFilters({ status: '', worker_id: '', department_id: '' })} className="text-xs sm:text-sm text-gray-500 hover:text-gray-700 underline">Clear</button>
      </div>

      {/* Task List */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {tasks.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No tasks found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-max">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 sm:px-5 py-2 sm:py-3">Task</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">Project</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">Assigned</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">Progress</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">Due</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {(search ? tasks.filter(t => [t.task_title, t.project_name, t.item_name, t.worker_name, t.department_name, t.proj_code].some(f => f?.toLowerCase().includes(search.toLowerCase()))) : tasks).map(t => {
                    const isDelayed = t.status !== 'completed' && t.due_date && t.due_date < today();
                    const pct = t.quantity_assigned > 0 ? Math.round((t.quantity_completed / t.quantity_assigned) * 100) : 0;
                    return (
                      <React.Fragment key={t.id}>
                      <tr className={`hover:bg-gray-50 text-xs sm:text-sm ${isDelayed ? 'bg-red-50/30' : ''}`}>
                        <td className="px-3 sm:px-5 py-2 sm:py-3">
                          <div className="flex items-center gap-1 sm:gap-2">
                            {isDelayed && <AlertTriangle className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-red-500 flex-shrink-0" />}
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">{t.task_title}</p>
                              {t.item_name && <p className="text-xs text-gray-400 truncate">📦 {t.item_name}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                          <button onClick={() => navigate(`/admin/projects/${t.project_id}`)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 line-clamp-1">
                            {t.project_name} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </button>
                          <p className="text-xs text-gray-400 font-mono truncate">{t.proj_code}</p>
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">
                          {t.assign_type === 'worker' && t.worker_name && (
                            <span className="text-gray-700 truncate block">👤 {t.worker_name}</span>
                          )}
                          {t.assign_type === 'department' && t.department_name && (
                            <span className="font-medium truncate block" style={{ color: t.dept_color }}>🏭 {t.department_name}</span>
                          )}
                          {t.assign_type === 'department' && t.worker_name && (
                            <span className="text-gray-500 text-xs truncate block">👤 {t.worker_name}</span>
                          )}
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                          <span className={`text-xs px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-medium capitalize status-${t.status}`}>{t.status}</span>
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">
                          <div className="flex items-center gap-1 sm:gap-2">
                            <div className="w-12 sm:w-16 bg-gray-100 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full ${t.status === 'completed' ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">{t.quantity_completed}/{t.quantity_assigned}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                          <span className={`text-xs sm:text-sm ${isDelayed ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{safeDate(t.due_date)}</span>
                        </td>
                        <td className="px-3 py-3">
                          <button onClick={() => togglePhotos(t.id)}
                            title="View photos"
                            className={`p-1.5 rounded-lg transition-colors ${showPhotosFor === t.id ? 'bg-blue-100 text-blue-600' : 'text-gray-300 hover:text-blue-500 hover:bg-blue-50'}`}>
                            <Camera className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      {showPhotosFor === t.id && (
                        <tr key={t.id + '_photos'}>
                          <td colSpan={8} className="px-5 py-3 bg-blue-50/50 border-b border-blue-100">
                            <p className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> {t.task_title} — Worker Photos</p>
                            {!taskPhotos[t.id]?.length ? (
                              <p className="text-xs text-gray-400">No photos uploaded yet</p>
                            ) : (
                              <div className="flex gap-2 flex-wrap">
                                {taskPhotos[t.id].map(img => (
                                  <div key={img.id} className="relative">
                                    <a href={img.image_url || buildImageUrl(img.image_path)} target="_blank" rel="noreferrer">
                                      <img src={img.image_url || buildImageUrl(img.image_path)} alt=""
                                        className="h-20 w-20 object-cover rounded-xl border border-blue-200 hover:opacity-90 cursor-pointer" />
                                    </a>
                                    <p className="text-xs text-gray-400 mt-0.5 text-center truncate w-20">{img.uploaded_by_name}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  
    </PermissionGuard>
  );
}
